/**
 * Composer model selection: what the thread's next turn will run on.
 *
 * The thread's lock if it has one, else the Draft's preference, else the sticky
 * preference, else the project's Default model, together with effort and the
 * options dispatch sends. This used to be assembled inside `ChatComposer` and
 * pulled back out through its imperative handle, which meant submission could
 * only ask the question after the Composer had rendered. It is a value now, so
 * the view that renders both the Composer and the submit handlers composes it
 * once and hands it to each.
 *
 * The pure half wraps `deriveEffectiveComposerModelState` rather than absorbing
 * it: that file owns the cross-provider guard and stays the one place the
 * precedence rule lives.
 *
 * Nothing here reads plan Usage Windows or the context ring. Its fields are
 * read on every render, which is exactly the shape that would turn a hover into
 * a refresh (`docs/adr/composer-plan-usage-is-read-only.md`).
 *
 * @module
 */
import type {
  ModelSelection,
  ProviderKind,
  ProviderModelOptions,
  ScopedThreadRef,
  ServerProvider,
  ServerProviderModel,
} from "@cmmd-forge/contracts";
import type { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import { useMemo } from "react";

import {
  type ComposerDraftModelSources,
  deriveEffectiveComposerModelState,
} from "../../composerModelState";
import { useComposerDraftModelState } from "../../composerDraftStore";
import { DraftId } from "../../draftId";
import { getProviderModels } from "../../providerModels";
import { getComposerProviderState } from "./composerProviderState";

export interface ComposerModelSelectionInput {
  readonly threadRef?: ScopedThreadRef | DraftId;
  readonly draftId?: DraftId;
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly resolvedProvider: ProviderKind;
  readonly lockedProvider: ProviderKind | null;
  readonly threadModelSelection: ModelSelection | null | undefined;
  readonly projectModelSelection: ModelSelection | null | undefined;
  readonly settings: UnifiedSettings;
}

export interface ComposerModelSelection {
  readonly provider: ProviderKind;
  readonly model: string;
  readonly models: ReadonlyArray<ServerProviderModel>;
  readonly promptEffort: string | null;
  readonly providerAccountId: string | undefined;
  /**
   * Options as the Draft holds them, before capability normalization. The
   * provider traits pickers edit these; `modelSelection.options` is the
   * normalized form dispatch sends.
   */
  readonly modelOptions: ProviderModelOptions | null;
  /** What dispatch sends, options and Provider Account folded in. */
  readonly modelSelection: ModelSelection;
}

export function deriveComposerModelSelection(input: {
  readonly draft: ComposerDraftModelSources | null | undefined;
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly resolvedProvider: ProviderKind;
  readonly lockedProvider: ProviderKind | null;
  readonly threadModelSelection: ModelSelection | null | undefined;
  readonly projectModelSelection: ModelSelection | null | undefined;
  readonly settings: UnifiedSettings;
}): ComposerModelSelection {
  const selectedProvider = input.lockedProvider ?? input.resolvedProvider;
  const effective = deriveEffectiveComposerModelState({
    draft: input.draft,
    providers: input.providers,
    selectedProvider,
    threadModelSelection: input.threadModelSelection,
    projectModelSelection: input.projectModelSelection,
    settings: input.settings,
  });
  const models = getProviderModels(input.providers, selectedProvider);
  // `prompt` only decides the ultrathink glow classNames, which this module does
  // not return. Taking a prompt would force its callers to subscribe to the
  // Draft's text and re-render on every keystroke; the Composer already does
  // that, so it calls getComposerProviderState a second time for the glow.
  const providerState = getComposerProviderState({
    provider: selectedProvider,
    model: effective.selectedModel,
    models,
    prompt: "",
    modelOptions: effective.modelOptions,
  });
  const modelSelection = {
    provider: selectedProvider,
    model: effective.selectedModel,
    ...(effective.selectedProviderAccountId
      ? { providerAccountId: effective.selectedProviderAccountId }
      : {}),
    ...(providerState.modelOptionsForDispatch
      ? { options: providerState.modelOptionsForDispatch }
      : {}),
  } as ModelSelection;

  return {
    provider: selectedProvider,
    model: effective.selectedModel,
    models,
    promptEffort: providerState.promptEffort,
    providerAccountId: effective.selectedProviderAccountId,
    modelOptions: effective.modelOptions,
    modelSelection,
  };
}

export function useComposerModelSelection(
  input: ComposerModelSelectionInput,
): ComposerModelSelection {
  const draft = useComposerDraftModelState(input.threadRef ?? input.draftId ?? DraftId.make(""));
  const {
    providers,
    resolvedProvider,
    lockedProvider,
    threadModelSelection,
    projectModelSelection,
    settings,
  } = input;

  return useMemo(
    () =>
      deriveComposerModelSelection({
        draft,
        providers,
        resolvedProvider,
        lockedProvider,
        threadModelSelection,
        projectModelSelection,
        settings,
      }),
    [
      draft,
      providers,
      resolvedProvider,
      lockedProvider,
      threadModelSelection,
      projectModelSelection,
      settings,
    ],
  );
}
